import { zodResolver } from "@hookform/resolvers/zod";
import { OploggSendToTodo } from "features/TodoOploggSidebar/Oplogg/OploggSendToTodo";
import { useOplogg } from "features/TodoOploggSidebar/Oplogg/useOplogg";
import { InputTextarea } from "features/TodoOploggSidebar/common/InputTextarea";
import { getDefaultReceivingRoles } from "features/TodoOploggSidebar/common/utils";
import { OploggForm, oploggForm } from "features/TodoOploggSidebar/formSchema";
import { Dispatch, SetStateAction, useCallback, useState } from "react";
import { FormProvider, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Button,
  Field,
  FieldErrorText,
  Flex,
  Grid,
  Text,
} from "shared/components/Spor";
import { useFormVerbose } from "shared/form/useFormVerbose";
import { useUserInfo } from "shared/hooks/useUserInfo";
import { UserRole } from "shared/types/roles";
import { useDropsRole } from "stores/useDropsRole";

interface OploggSendMessageProps {
  setHeaderState: Dispatch<SetStateAction<"new-message" | "search" | null>>;
}

const MESSAGE_CLOSE_DELAY_MS = 500;

export const OploggSendMessage = ({
  setHeaderState,
}: OploggSendMessageProps) => {
  const { role: currentUserRole } = useDropsRole();
  const { post, postStatus, postToDo } = useOplogg();
  const [sendToTodo, setSendToTodo] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<UserRole[]>(
    getDefaultReceivingRoles(currentUserRole),
  );
  const [vehicleSetTypes, setVehicleSetTypes] = useState<string[]>([]);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const { displayName } = useUserInfo();

  const closeMessaging = useCallback(() => {
    setTimeout(() => {
      setHeaderState(null);
    }, MESSAGE_CLOSE_DELAY_MS);
  }, [setHeaderState]);

  const formMethods = useFormVerbose<OploggForm>({
    resolver: zodResolver(oploggForm),
    defaultValues: {
      message: "",
      author:
        currentUserRole === UserRole.oks ? `${displayName} (OKS)` : displayName,
    },
  });

  const { reset, handleSubmit } = formMethods;
  const { t } = useTranslation();

  const submitHandler: SubmitHandler<OploggForm> = useCallback(
    (data) => {
      if (sendToTodo && selectedRoles.length === 0) {
        setRolesError("Du må velge en rolle");
        return;
      }
      setRolesError(null);

      post(data);

      if (sendToTodo) {
        const todoData = {
          description: data.message,
          roles: selectedRoles,
          vehicleSetTypes: vehicleSetTypes.join(","),
          username: data.author,
        };
        postToDo(todoData);
      }
      closeMessaging();
      reset();
    },
    [
      sendToTodo,
      post,
      postToDo,
      selectedRoles,
      closeMessaging,
      reset,
      vehicleSetTypes,
    ],
  );

  const onSubmit = handleSubmit(submitHandler);

  return (
    <Grid gridArea="todooplogg" width="100%">
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmit}>
          <Flex direction="column" gap={2} p={2}>
            <Text fontWeight="bold">{t("Ny melding")}</Text>
            <InputTextarea
              label={t("Skriv inn tekst her...")}
              formField="message"
            />

            <Flex as="label" alignItems="center" cursor="pointer" gap={1}>
              <Checkbox
                checked={sendToTodo}
                onCheckedChange={() => setSendToTodo((previous) => !previous)}
              />
              <Text variant="sm">{t("Send også til To-do")}</Text>
            </Flex>

            {sendToTodo && (
              <>
                <OploggSendToTodo
                  selectedRoles={selectedRoles}
                  setSelectedRoles={(roles) => {
                    setSelectedRoles(roles);
                    setRolesError(null);
                  }}
                  vehicleSetTypes={vehicleSetTypes}
                  setVehicleSetTypes={setVehicleSetTypes}
                />
                {rolesError && (
                  <Field invalid>
                    <FieldErrorText>{rolesError}</FieldErrorText>
                  </Field>
                )}
              </>
            )}

            <Button
              type="submit"
              size="sm"
              variant="primary"
              loading={postStatus === "pending"}
              data-testid="submit-button-oplogg"
              alignSelf="flex-end"
            >
              {t("Send")}
            </Button>
          </Flex>
        </form>
      </FormProvider>
    </Grid>
  );
};
